import { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  Tooltip,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Input,
} from '@fluentui/react-components';
import {
  BoxMultipleRegular,
  ClipboardTaskListLtrRegular,
  ListRegular,
  SettingsRegular,
  QuestionCircleRegular,
  MailRegular,
  BookQuestionMarkRegular,
  ChatHelpRegular,
  LinkRegular,
  MoneyCalculatorRegular,
  TaskListSquareLtrRegular,
  WrenchScrewdriverRegular,
  BoardRegular,
  BookDatabaseRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ZoomInRegular,
  ZoomOutRegular,
  PeopleSwapRegular,
  DismissRegular,
  SearchRegular,
  EyeRegular,
  VideoRegular,
  DocumentRegular,
  PersonSupportRegular,
  CallRegular,
  OpenRegular,
  ReceiptMoneyRegular,
  LightbulbRegular,
  HatGraduationRegular,
  TargetArrowRegular,
  WarningRegular,
  DataPieRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../../../providers/AuthProvider';
import { useOrgContext } from '../../../providers/DataverseOrgProvider';
import { dataverseClient } from '../../../api';
import { escapeODataString, sanitizeUserSearchQuery } from '../../../api/dataverse/utils';
import { COLORS, dark, veracityColors, isLightColor, VeraBrand, ModuleSwitcher, UserMenu } from '../../../ui';
import {
  useAreaTracking,
  stripTrailingSlash,
  moduleBaseForPath,
} from '../../../entities/user-activity';
import { useHelpPanel, useModuleAccent } from '../../../entities/admin-config';
import type { HelpPanelIconKey, HelpPanelLink } from '../../../entities/admin-config';
import { rainOverlay, getSeasonalEffect, runAcross, firstNameOf } from '../../../lib';
import { BroadcastBanner, BroadcastBell } from '../../broadcast';
// Straight at the module, NOT a barrel: a barrel that also exports the guide
// PAGE would pull BlockRenderer and the whole markdown stack into the main
// chunk (352KB -> 526KB when this was measured on 2026-08-16).
import { ModuleGuideNavItem } from '../../module-guide/ui/ModuleGuideNavItem';

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  
  // Header
  header: {
    height: '48px',
    backgroundColor: COLORS.primary,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  waffleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  appTitle: {
    color: COLORS.white,
    fontSize: '16px',
    fontWeight: '600',
  },
  appTitleOnLight: {
    color: veracityColors.midnightCharcoal,
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  // Deliberately obnoxious: Lindsay kept having to open the user menu to
  // confirm which org she was on. Yellow + black reads as "warning" against
  // every module's header color.
  devPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#FFD400',
    color: '#1C1919',
    fontSize: '13px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '5px 14px',
    borderRadius: '999px',
    border: '2px solid #1C1919',
    whiteSpace: 'nowrap',
    marginLeft: '12px',
  },
  envBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '4px 16px',
    marginRight: '8px',
  },
  envLabel: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.7)',
  },
  envValue: {
    fontSize: '13px',
    color: COLORS.white,
    fontWeight: '500',
  },
  headerIconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
  },
  headerIconBtnOnLight: {
    color: veracityColors.midnightCharcoal,
    ':hover': {
      backgroundColor: 'rgba(28,25,25,0.08)',
    },
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 12px 4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
  },
  userName: {
    fontSize: '13px',
    fontWeight: '500',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  
  // Main Layout
  mainLayout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    // Flex item of the 100vh column — without min-height:0 it refuses to
    // shrink below its content's intrinsic height, which lets a tall page
    // push past the viewport and produces a second (outer) scrollbar.
    minHeight: 0,
  },
  
  // Sidebar
  sidebar: {
    width: '220px',
    backgroundColor: dark.bgBase,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    transitionProperty: 'width',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  sidebarCollapsed: {
    width: '48px',
  },
  navGroup: {
    padding: '8px 0',
    borderBottom: `1px solid ${dark.border}`,
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    color: 'rgba(255,241,224,0.85)',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '0.1s',
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: dark.bgSurface,
    },
  },
  navItemCollapsed: {
    padding: '10px 0',
    justifyContent: 'center',
    gap: '0',
  },
  navItemSelected: {
    backgroundColor: dark.bgElevated,
    borderLeft: '3px solid var(--app-accent, #D6461D)',
    paddingLeft: '13px',
  },
  navItemSelectedCollapsed: {
    paddingLeft: '0',
    borderLeft: '3px solid var(--app-accent, #D6461D)',
  },
  navItemDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  navIcon: {
    fontSize: '20px',
    flexShrink: 0,
    opacity: 0.9,
  },
  navLabel: {
    flex: 1,
    overflow: 'hidden',
  },
  navLabelHidden: {
    display: 'none',
  },
  navBadge: {
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    // ADO #1610, second pass. The first pass moved these numerals off
    // textMuted (~2.5:1 on the 15% white chip, unreadable) up to
    // textSecondary, which measures ~4.9:1. That clears the WCAG floor, but
    // Lindsay had still not signed off three weeks later, and a 10px numeral
    // sitting at the bare minimum is a count you squint at rather than read.
    // So the numerals now take the nav label's own color, ~7.7:1 on the chip,
    // one size up and semibold. Lindsay asked for an orange chip; Sasha's
    // call both times was to keep the chip subtle and spend the contrast on
    // the number, so the background is untouched. Matching the label rather
    // than going all the way to First Light keeps the hierarchy right: the
    // count reads as part of its nav item, not louder than it.
    color: 'rgba(255,241,224,0.85)',
  },
  navSubItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px 6px 44px',
    color: dark.textMuted,
    fontSize: '12px',
    textDecoration: 'none',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  },
  navSubItemSelected: {
    color: '#fff',
    fontWeight: '600',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  navSectionHeader: {
    padding: '14px 16px 4px',
    fontSize: '11px',
    fontWeight: 600,
    color: dark.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    userSelect: 'none',
  },
  navSectionDivider: {
    height: '1px',
    margin: '10px 12px 6px',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  /** A collapsible section heading: the same type, made clickable. */
  navSectionToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { color: 'rgba(255,255,255,0.9)' },
  },
  navSectionChevron: {
    fontSize: '10px',
    transition: 'transform 120ms ease',
  },
  collapseToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    cursor: 'pointer',
    borderTop: `1px solid rgba(255,255,255,0.15)`,
    ':hover': {
      color: '#fff',
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  seasonalTrigger: {
    display: 'block',
    width: '100%',
    height: '16px',
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  
  // Content — the single scroll region for the app body.
  //
  // Explicit flex column so a page whose root is `height: 100%` resolves its
  // height against a definite parent and fills exactly (its own inner
  // overflow:auto region does the scrolling — one scrollbar). `min-height: 0`
  // + `min-width: 0` let this flex item shrink to the viewport instead of
  // being forced taller/wider by a full-height page or a wide table, which is
  // what produced the double scrollbar. Flowing pages (no height:100%) still
  // scroll here via overflow:auto.
  content: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    backgroundColor: COLORS.bgBase,
  },
  
  // Help Dialog
  helpSection: {
    marginBottom: '20px',
  },
  helpSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginBottom: '12px',
  },
  helpLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: COLORS.textPrimary,
    textDecoration: 'none',
    ':hover': {
      backgroundColor: COLORS.bgBase,
    },
  },
  helpLinkIcon: {
    fontSize: '20px',
    color: COLORS.primary,
  },
  helpLinkText: {
    flex: 1,
  },
  helpLinkTitle: {
    fontSize: '14px',
    fontWeight: '500',
  },
  helpLinkDesc: {
    fontSize: '12px',
    color: COLORS.textSecondary,
  },
  
  // User menu header
  userMenuHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: '4px',
  },
  userMenuName: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.textPrimary,
  },
  userMenuEmail: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    marginTop: '2px',
  },

  // App Launcher
  launcherPanel: {
    padding: '16px',
    width: '280px',
  },
  launcherTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginBottom: '12px',
  },
  launcherGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '4px',
  },
  launcherTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: COLORS.bgSurface,
    },
  },
  launcherTileCurrent: {
    backgroundColor: COLORS.bgSurface,
    boxShadow: `inset 0 0 0 1.5px ${veracityColors.veracityOrange}`,
  },
  launcherTileIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#FFFFFF',
  },
  launcherTileLabel: {
    fontSize: '11px',
    color: COLORS.textPrimary,
    textAlign: 'center',
    lineHeight: '1.2',
    fontWeight: '500',
  },

  // Impersonation Banner
  impersonationBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '6px 16px',
    backgroundColor: '#D6461D',
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: '500',
    flexShrink: 0,
  },
  impersonationStopBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '4px',
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.35)',
    },
  },

  // Impersonate Dialog
  userSearchResults: {
    maxHeight: '300px',
    overflowY: 'auto',
    marginTop: '12px',
  },
  userSearchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: COLORS.bgBase,
    },
  },
  userSearchAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: COLORS.bgSurface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: COLORS.textSecondary,
    flexShrink: 0,
  },
  userSearchInfo: {
    flex: 1,
    minWidth: 0,
  },
  userSearchName: {
    fontSize: '14px',
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  userSearchEmail: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Easter egg greeting (Konami)
  eggGreeting: {
    position: 'fixed',
    top: '64px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 100000,
    padding: '10px 18px',
    borderRadius: '999px',
    backgroundColor: 'rgba(28,25,25,0.9)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
    letterSpacing: '0.3px',
    pointerEvents: 'none',
    animationName: {
      '0%': { opacity: 0, transform: 'translate(-50%, -8px)' },
      '10%': { opacity: 1, transform: 'translate(-50%, 0)' },
      '85%': { opacity: 1, transform: 'translate(-50%, 0)' },
      '100%': { opacity: 0, transform: 'translate(-50%, -8px)' },
    },
    animationDuration: '3.2s',
    animationTimingFunction: 'ease',
    animationFillMode: 'forwards',
  },
  veraMarkClickable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
});

// =============================================================================
// App Launcher Config
// =============================================================================

export type VeraAppId = 'catalog' | 'ops' | 'sales' | 'rfp' | 'maple' | 'ideas' | 'marketing' | 'finance' | 'academy' | 'portfolio' | 'share' | 'rx' | 'analytics' | 'desk';

interface VeraApp {
  id: VeraAppId;
  label: string;
  icon: typeof BoxMultipleRegular;
  color: string;
}

const VERA_APPS: VeraApp[] = [
  {
    id: 'sales',
    label: 'Vera Sales',
    icon: MoneyCalculatorRegular,
    color: '#553746',
  },
  {
    id: 'ops',
    label: 'Vera Ops',
    icon: TaskListSquareLtrRegular,
    color: COLORS.primary,
  },
  {
    id: 'catalog',
    label: 'Vera Catalog',
    icon: BookDatabaseRegular,
    color: '#3F4C5C',
  },
  {
    id: 'rfp',
    label: 'Vera Proposal',
    icon: ClipboardTaskListLtrRegular,
    color: veracityColors.clarityGreen,
  },
  {
    id: 'rx',
    // "Rx Savings Analysis" since 9/27, display only (see moduleCatalogData).
    label: 'Rx Savings Analysis',
    icon: ReceiptMoneyRegular,
    color: veracityColors.clarityGreen,
  },
  {
    // Renamed from Vera Clinical 9/04 (pre-deploy): took over the id the
    // hidden analytics placeholder had reserved in the module catalog.
    id: 'analytics',
    // "Claims Analysis" since 9/27, display only (see moduleCatalogData).
    label: 'Claims Analysis',
    icon: DataPieRegular,
    color: veracityColors.duskBlue,
  },
  {
    id: 'maple',
    label: 'Maple',
    icon: ReceiptMoneyRegular,
    color: veracityColors.midnightCharcoal,
  },
  {
    id: 'finance',
    label: 'Vera Finance',
    icon: ReceiptMoneyRegular,
    color: veracityColors.clarityGreen,
  },
  {
    id: 'ideas',
    label: 'Vera Ideas',
    icon: LightbulbRegular,
    color: veracityColors.daybreakAmber,
  },
  {
    id: 'marketing',
    label: 'Vera Marketing',
    icon: BoxMultipleRegular,
    color: veracityColors.horizonBlue,
  },
  {
    id: 'academy',
    label: 'Vera Academy',
    icon: HatGraduationRegular,
    // Morning Coral: light enough that the YIQ check below flips the header
    // foregrounds to Midnight Charcoal automatically.
    color: veracityColors.morningCoral,
  },
  {
    id: 'portfolio',
    label: 'Vera Portfolio',
    icon: TargetArrowRegular,
    color: veracityColors.twilightPlum,
  },
];


// =============================================================================
// Navigation Items
// =============================================================================

export interface NavItem {
  path: string;
  label: string;
  icon: typeof BoxMultipleRegular;
  disabled?: boolean;
  badge?: string;
  children?: { path: string; label: string }[];
}

/**
 * A grouping label rendered between nav items. Expanded: a small uppercase
 * section heading; collapsed sidebar: a thin divider (no room for text).
 * Existing callers that pass `NavItem[]` keep working unchanged, and a section
 * without `collapsible` behaves exactly as it always has.
 *
 * COLLAPSIBLE (Sasha, 9/20, for the Ops nav: "so the overall list is less long
 * and overwhelming"). The section heading becomes a button that hides and
 * shows every item under it, up to the next section. Sales gets the same
 * effect from parent items with `children`, but that pattern needs the parent
 * to BE a page, and Ops sections are labels with no route of their own.
 *
 * Two rules keep it from hiding where you are: a section holding the active
 * route is always open regardless of its state, and a collapsed icon rail
 * ignores the whole mechanism, because there are no headings to click and
 * hiding the icons would leave nothing to navigate with.
 *
 * The open state persists per module in localStorage, so the choice survives
 * a reload.
 */
export interface NavSection {
  kind: 'section';
  label: string;
  /** Make the heading a toggle. Omitted = the old always-visible behaviour. */
  collapsible?: boolean;
  /** Only meaningful with `collapsible`. Defaults to open. */
  defaultOpen?: boolean;
}

export type NavEntry = NavItem | NavSection;

function isNavSection(entry: NavEntry): entry is NavSection {
  return 'kind' in entry && entry.kind === 'section';
}

const defaultNavItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: BoardRegular },
  { path: '/solutions', label: 'Solutions', icon: BoxMultipleRegular },
  { path: '/group-info', label: 'Group Info Template', icon: ClipboardTaskListLtrRegular },
  { path: '/implementation', label: 'Implementation', icon: WrenchScrewdriverRegular },
  { path: '/managed-lists', label: 'Managed Lists', icon: ListRegular },
  { path: '/partner-ids', label: 'Partner IDs', icon: LinkRegular },
  { path: '/demo-preview', label: 'Demo Preview', icon: EyeRegular },
  { path: '/admin-config', label: 'Configuration', icon: SettingsRegular },
];

// =============================================================================
// Helper Functions
// =============================================================================

// Map Help Panel icon keys → Fluent icon components. Must stay in sync with
// the editor's ICON_COMPONENTS in catalog/admin-config/HelpPanelEditor.tsx.
const HELP_PANEL_ICON_COMPONENTS: Record<HelpPanelIconKey, typeof BookQuestionMarkRegular> = {
  book: BookQuestionMarkRegular,
  mail: MailRegular,
  chat: ChatHelpRegular,
  video: VideoRegular,
  document: DocumentRegular,
  person: PersonSupportRegular,
  call: CallRegular,
  link: LinkRegular,
  open: OpenRegular,
  question: QuestionCircleRegular,
};

function classifyHelpHref(href: string): 'mailto' | 'internal' | 'external' | 'empty' {
  const t = href.trim();
  if (!t) return 'empty';
  if (t.startsWith('mailto:')) return 'mailto';
  if (t.startsWith('/')) return 'internal';
  return 'external';
}

/**
 * Render a help link as the appropriate element:
 *  - internal → div + onClick (uses passed navigate callback, closes dialog)
 *  - mailto/external → <a> (mailto opens client; external opens new tab)
 */
function renderHelpLink(
  link: HelpPanelLink,
  kind: 'mailto' | 'internal' | 'external',
  content: ReactNode,
  className: string,
  onInternalClick: () => void,
): ReactNode {
  if (kind === 'internal') {
    return (
      <div
        key={link.id}
        className={className}
        style={{ cursor: 'pointer' }}
        onClick={onInternalClick}
      >
        {content}
      </div>
    );
  }
  return (
    <a
      key={link.id}
      href={link.href}
      className={className}
      target={kind === 'external' ? '_blank' : undefined}
      rel={kind === 'external' ? 'noopener noreferrer' : undefined}
    >
      {content}
    </a>
  );
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return (parts[0] ?? '').substring(0, 2).toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

// =============================================================================
// Component
// =============================================================================

interface AppShellProps {
  children: ReactNode;
  appId?: VeraAppId;
  appTitle?: string;
  navItems?: NavEntry[];
  headerExtra?: ReactNode;
  /** Optional search widget rendered in the header, left of the env badge / headerExtra. */
  headerSearch?: ReactNode;
  headerColor?: string;
  /**
   * Where this module is mounted, when that is not `/${appId}` (rfp mounts
   * at /proposal). Only the Guide entry needs it.
   */
  guideBasePath?: string;
}

export function AppShell({ children, appId = 'catalog', appTitle: appTitleProp, navItems = defaultNavItems, headerExtra, headerSearch, headerColor: headerColorProp, guideBasePath }: AppShellProps) {
  // Resolve title + header color. Precedence: an explicit prop (a page that
  // deliberately overrides), then the admin's chosen module color from
  // Catalog > Configuration > Launcher categories, then the locked VERA_APPS
  // default. The admin value flows on to the `--app-accent` custom property
  // below, so it reaches every rule inside the module that reads it, not just
  // the header bar.
  const appTitle = appTitleProp ?? VERA_APPS.find((a) => a.id === appId)?.label ?? 'Vera Catalog';
  const adminAccent = useModuleAccent(appId);
  const headerColor = headerColorProp ?? adminAccent ?? VERA_APPS.find((a) => a.id === appId)?.color;
  // Light header colors (e.g. Daybreak Amber for Ideas) take charcoal
  // foregrounds. Derived by luminance rather than stored, so an admin picking
  // any approved color keeps brand guide 4.5 satisfied without knowing the rule.
  const lightHeader = !!headerColor && isLightColor(headerColor);
  const { activeOrg } = useOrgContext();
  const styles = useStyles();
  const headerBtnClass = mergeClasses(styles.headerIconBtn, lightHeader && styles.headerIconBtnOnLight);
  const location = useLocation();
  const navigate = useNavigate();

  // Auth
  const { email, displayName, impersonation, startImpersonation, stopImpersonation } = useAuth();
  const helpPanel = useHelpPanel();

  const firstName = firstNameOf(displayName) || 'friend';

  // State
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonateSearch, setImpersonateSearch] = useState('');
  const [impersonateResults, setImpersonateResults] = useState<Array<{ oid: string; name: string; email: string }>>([]);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem(`vera-${appId}-sidebar-collapsed`) === 'true';
  });

  /**
   * Which collapsible nav sections the user has opened or closed, by label.
   * Only sections they have actually toggled are stored; anything absent falls
   * back to the caller's `defaultOpen`, so changing a default later is not
   * silently overridden by a stale preference.
   */
  const sectionStateKey = `vera-${appId}-nav-sections`;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(sectionStateKey);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  const toggleSection = useCallback((label: string, next: boolean) => {
    setOpenSections((prev) => {
      const updated = { ...prev, [label]: next };
      try {
        localStorage.setItem(sectionStateKey, JSON.stringify(updated));
      } catch {
        // A browser refusing storage should not break the nav.
      }
      return updated;
    });
  }, [sectionStateKey]);

  // =====================================================================
  // Easter eggs
  // - Konami code → confetti burst + "Hi, <FirstName>!" greeting
  // - Triple-click the app title text → toggle retro mode (CRT scanlines + neon)
  // =====================================================================
  const [eggGreeting, setEggGreeting] = useState<string | null>(null);
  const [retroMode, setRetroMode] = useState(() => localStorage.getItem('vera-retro-mode') === 'true');
  const [discoGif, setDiscoGif] = useState<string | null>(null);
  const veraClickRef = useRef<{ count: number; first: number }>({ count: 0, first: 0 });
  const seasonal = getSeasonalEffect();

  // Inject retro-mode stylesheet once
  useEffect(() => {
    const id = 'vera-retro-mode-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Monoton&display=swap');
      html[data-retro="true"] body { font-family: 'Courier New', monospace !important; }
      html[data-retro="true"] header { box-shadow: 0 0 12px #ff00ff, 0 0 24px #00ffff, inset 0 -2px 0 rgba(255,255,255,0.3) !important; }
      html[data-retro="true"] [data-retro-title] {
        font-family: 'Monoton', 'Brush Script MT', cursive !important;
        font-weight: 400 !important;
        font-size: 22px !important;
        letter-spacing: 2px !important;
        color: #fff5fb !important;
        text-shadow:
          0 0 4px #fff,
          0 0 8px #ff00de,
          0 0 16px #ff00de,
          0 0 28px #ff00de,
          0 0 44px #ff00de !important;
        animation: vera-neon-flicker 3.6s infinite alternate;
      }
      @keyframes vera-neon-flicker {
        0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% {
          opacity: 1;
          text-shadow:
            0 0 4px #fff,
            0 0 8px #ff00de,
            0 0 16px #ff00de,
            0 0 28px #ff00de,
            0 0 44px #ff00de;
        }
        20%, 21.999%, 63%, 63.999%, 65%, 69.999% {
          opacity: 0.72;
          text-shadow: none;
        }
      }
      html[data-retro="true"] {
        filter: contrast(1.05) saturate(1.2);
      }
      html[data-retro="true"] body::before {
        content: "";
        position: fixed; inset: 0;
        background: repeating-linear-gradient(0deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 3px);
        pointer-events: none;
        z-index: 99998;
        mix-blend-mode: multiply;
      }
      html[data-retro="true"] body::after {
        content: "";
        position: fixed; inset: 0;
        background: radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.35) 100%);
        pointer-events: none;
        z-index: 99999;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Apply/persist retro-mode attribute
  useEffect(() => {
    if (retroMode) document.documentElement.setAttribute('data-retro', 'true');
    else document.documentElement.removeAttribute('data-retro');
    localStorage.setItem('vera-retro-mode', String(retroMode));
  }, [retroMode]);

  // Konami listener: ↑↑↓↓←→←→BA
  // Also watches a rolling buffer of letter keys for typed words like "danceparty".
  useEffect(() => {
    const sequence = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let pos = 0;
    let typedBuffer = ''; // rolling buffer for typed-word eggs
    const TYPED_WORDS = ['danceparty', 'rawr', 'roar', 'tinysasha'];
    const MAX_BUFFER = 20;
    const handler = (e: KeyboardEvent) => {
      const expected = sequence[pos];
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      // Konami sequence match
      if (key === expected) {
        pos++;
        if (pos === sequence.length) {
          pos = 0;
          confetti({
            particleCount: 140,
            spread: 90,
            origin: { y: 0.25 },
            zIndex: 100001,
          });
          setEggGreeting(`You found it, ${firstName}!`);
          setTimeout(() => setEggGreeting(null), 3200);
        }
      } else {
        pos = key === sequence[0] ? 1 : 0;
      }

      // Typed-word buffer (ignore when user is in an input)
      const t = e.target as HTMLElement | null;
      const inField =
        !!t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (!inField && /^[a-z]$/.test(key)) {
        typedBuffer = (typedBuffer + key).slice(-MAX_BUFFER);
        for (const word of TYPED_WORDS) {
          if (typedBuffer.endsWith(word)) {
            typedBuffer = '';
            if (word === 'danceparty') {
              // Available files in /public — numbers are non-contiguous (no 9).
              const discoNums = [1, 2, 3, 4, 5, 6, 7, 8, 10];
              const pick = discoNums[Math.floor(Math.random() * discoNums.length)]!;
              setDiscoGif(`/disco${pick}.gif`);
            } else if (word === 'rawr' || word === 'roar') {
              runAcross({ kind: 'emoji', char: '🦖' });
            } else if (word === 'tinysasha') {
              // Parade: 12 tiny sashas marching across, evenly spaced.
              runAcross(
                { kind: 'image', src: '/tiny-sasha.png' },
                { size: 90, count: 12, staggerMs: 380, duration: 5 },
              );
            }
            break;
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [firstName]);

  const handleSeasonalRain = () => {
    rainOverlay({ kind: 'emoji', char: seasonal.emoji });
  };

  const handleVeraMarkClick = () => {
    const now = Date.now();
    const state = veraClickRef.current;
    if (now - state.first > 600) {
      state.count = 1;
      state.first = now;
      return;
    }
    state.count++;
    if (state.count >= 3) {
      state.count = 0;
      state.first = 0;
      setRetroMode((v) => !v);
    }
  };

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem(`vera-${appId}-sidebar-collapsed`, String(next));
  };

  // =====================================================================
  // User activity tracking — emit area_visit on nav changes.
  // The matching rules now live in the hook, which the shell's own chrome
  // (launcher, /admin) shares so those pages stop being invisible.
  // =====================================================================
  useAreaTracking(navItems);

  // Zoom
  const ZOOM_STEPS = [0.75, 0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2, 1.25];
  const ZOOM_DEFAULT = 1;
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('vera-zoom');
    return saved ? Number(saved) : ZOOM_DEFAULT;
  });
  const zoomIn = () => {
    const idx = ZOOM_STEPS.indexOf(zoom);
    const next = idx < ZOOM_STEPS.length - 1 ? ZOOM_STEPS[idx + 1] ?? zoom : zoom;
    setZoom(next);
    localStorage.setItem('vera-zoom', String(next));
  };
  const zoomOut = () => {
    const idx = ZOOM_STEPS.indexOf(zoom);
    const next = idx > 0 ? ZOOM_STEPS[idx - 1] ?? zoom : zoom;
    setZoom(next);
    localStorage.setItem('vera-zoom', String(next));
  };
  const resetZoom = () => {
    setZoom(ZOOM_DEFAULT);
    localStorage.setItem('vera-zoom', String(ZOOM_DEFAULT));
  };

  // Impersonation: search Dataverse system users
  const searchUsers = useCallback(async (query: string) => {
    const safe = sanitizeUserSearchQuery(query);
    if (safe.length < 2) {
      setImpersonateResults([]);
      return;
    }
    setImpersonateLoading(true);
    try {
      const result = await dataverseClient.list<{
        systemuserid: string;
        fullname: string;
        internalemailaddress: string;
        azureactivedirectoryobjectid: string;
        isdisabled: boolean;
      }>('systemusers', {
        filter: `(contains(fullname,'${escapeODataString(safe)}') or contains(internalemailaddress,'${escapeODataString(safe)}')) and isdisabled eq false and azureactivedirectoryobjectid ne null`,
        select: ['systemuserid', 'fullname', 'internalemailaddress', 'azureactivedirectoryobjectid'],
        top: 20,
        orderBy: 'fullname asc',
      });
      setImpersonateResults(
        result.value
          .filter((u) => u.azureactivedirectoryobjectid)
          .map((u) => ({
            oid: u.azureactivedirectoryobjectid,
            name: u.fullname || 'Unknown',
            email: u.internalemailaddress || '',
          }))
      );
    } catch (err) {
      console.error('User search failed:', err);
      setImpersonateResults([]);
    } finally {
      setImpersonateLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!impersonateDialogOpen) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchUsers(impersonateSearch), 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [impersonateSearch, impersonateDialogOpen, searchUsers]);

  const handlePickUser = async (target: { oid: string; name: string; email: string }) => {
    try {
      await startImpersonation(target);
      setImpersonateDialogOpen(false);
      setImpersonateSearch('');
      setImpersonateResults([]);
      window.location.reload(); // clean reload to reset all queries
    } catch (err) {
      console.error('Failed to start impersonation:', err);
    }
  };

  const handleStopImpersonation = async () => {
    setStoppingImpersonation(true);
    try {
      await stopImpersonation();
      window.location.reload();
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      setStoppingImpersonation(false);
    }
  };

  // Nav paths are matched by prefix so a detail route keeps its parent item lit
  // (e.g. /ops/groups/<id> lights "All Groups"). When one nav path is a prefix
  // of another (Finance: /monitoring and /monitoring/sources), that would light
  // both, so the most specific match wins: an item is active only if no longer
  // nav path also matches the current location.
  //
  // Trailing slashes are stripped first. Every module builds its root item as
  // `${basePath}/` ('/ops/'), which the router never reports, so the module's
  // own Dashboard item never lit on the page people land on. That same
  // mismatch is what logged 'Unknown' telemetry for every module landing page
  // (ADO #1558). The root then lights on an exact match only, because it
  // prefixes every route in the module.
  const currentPath = stripTrailingSlash(location.pathname);
  const moduleBase = moduleBaseForPath(currentPath);
  const matchesPath = (raw: string) => {
    const path = stripTrailingSlash(raw);
    if (path === moduleBase) return currentPath === path;
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const allNavPaths = navItems
    .flatMap((entry) =>
      isNavSection(entry) ? [] : [entry.path, ...(entry.children?.map((c) => c.path) ?? [])]
    )
    .map(stripTrailingSlash);

  const isActive = (raw: string) => {
    const path = stripTrailingSlash(raw);
    if (!matchesPath(path)) return false;
    return !allNavPaths.some((other) => other.length > path.length && matchesPath(other));
  };

  const entryIsActive = (entry: NavEntry): boolean =>
    !isNavSection(entry)
    && (isActive(entry.path) || (entry.children?.some((c) => isActive(c.path)) ?? false));

  /**
   * Collapsible sections (Sasha, 9/20). The nav list is flat, so an item
   * belongs to the nearest section above it; this is that lookup, by index.
   */
  const sectionForIndex: (NavSection | null)[] = [];
  {
    let current: NavSection | null = null;
    for (const entry of navItems) {
      if (isNavSection(entry)) current = entry;
      sectionForIndex.push(isNavSection(entry) ? entry : current);
    }
  }

  /**
   * What a closed section is hiding, as a number. Badges are the "you have
   * work waiting" signal (the Ops approval queue's pending count among them),
   * so a collapsed section that swallowed them would quietly cost an approver
   * the only prompt they get. Summed here and shown on the heading instead.
   */
  const sectionBadgeTotals = new Map<string, number>();
  navItems.forEach((entry, idx) => {
    if (isNavSection(entry) || !entry.badge) return;
    const section = sectionForIndex[idx];
    if (!section?.collapsible) return;
    const n = Number(entry.badge);
    if (!Number.isFinite(n) || n <= 0) return;
    sectionBadgeTotals.set(section.label, (sectionBadgeTotals.get(section.label) ?? 0) + n);
  });

  const isSectionOpen = (section: NavSection): boolean => {
    // A collapsed icon rail has no headings to click, so hiding the icons
    // would leave nothing to navigate with.
    if (!section.collapsible || sidebarCollapsed) return true;
    return openSections[section.label] ?? section.defaultOpen ?? true;
  };

  return (
    <div className={styles.container} style={headerColor ? { '--app-accent': headerColor } as React.CSSProperties : undefined}>
      {/* Header */}
      <header className={styles.header} style={headerColor ? { backgroundColor: headerColor } : undefined}>
        <div className={styles.headerLeft}>
          <ModuleSwitcher onDark={!lightHeader} />
          <VeraBrand
            onDark={!lightHeader}
            markColor={lightHeader ? veracityColors.midnightCharcoal : undefined}
          />
          {/* Mascot head + divider retired (7/7/26 item 15d): the VeraBrand
              triangle to the left is the one mark. The triple-click retro egg
              lives on the title text. */}
          <span
            className={mergeClasses(styles.appTitle, styles.veraMarkClickable, lightHeader && styles.appTitleOnLight)}
            data-retro-title
            onClick={handleVeraMarkClick}
            title={retroMode ? 'Retro mode on' : undefined}
          >
            {retroMode ? `${appTitle} Retro Mode` : appTitle}
          </span>
          {activeOrg === 'dev' && (
            <span
              className={styles.devPill}
              title="Every read and write on this tab targets the DEV Dataverse org. Switch back to production from your account menu."
            >
              <WarningRegular style={{ fontSize: '16px' }} />
              Dev Data
            </span>
          )}
        </div>

        <div className={styles.headerRight}>
          {headerSearch && <div style={{ marginRight: '8px' }}>{headerSearch}</div>}
          {/* Environment switcher relocated into the user menu (rarely needed
              now that we're live). Modules may still inject headerExtra. */}
          {headerExtra}

          {/* Reaches a dismissed-but-still-live announcement. Renders nothing
              when there is nothing live, so it costs no header space the rest
              of the time. Mounted in ShellLayout too. */}
          <BroadcastBell className={headerBtnClass} />


          {/* Zoom controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginRight: '4px' }}>
            <button
              className={headerBtnClass}
              title="Zoom out"
              onClick={zoomOut}
              disabled={zoom <= (ZOOM_STEPS[0] ?? 0)}
            >
              <ZoomOutRegular style={{ fontSize: '18px' }} />
            </button>
            <button
              className={headerBtnClass}
              title="Reset zoom"
              onClick={resetZoom}
              style={{ fontSize: '11px', minWidth: '36px', fontWeight: zoom !== ZOOM_DEFAULT ? 600 : 400 }}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              className={headerBtnClass}
              title="Zoom in"
              onClick={zoomIn}
              disabled={zoom >= (ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? Infinity)}
            >
              <ZoomInRegular style={{ fontSize: '18px' }} />
            </button>
          </div>

          <button
            className={headerBtnClass}
            title="Help"
            onClick={() => setHelpDialogOpen(true)}
          >
            <QuestionCircleRegular style={{ fontSize: '20px' }} />
          </button>

          {/* Settings gear retired — "View as another user" now lives in the
              user menu; each module's Application Settings moved into its own
              left-nav area (Catalog → Activity Log, Sales → Admin ▸ Reports). */}
          <UserMenu onDark={!lightHeader} onViewAsUser={() => setImpersonateDialogOpen(true)} />
        </div>
      </header>

      {/* Easter egg greeting (Konami) */}
      {eggGreeting && (
        <div className={styles.eggGreeting} key={eggGreeting}>{eggGreeting}</div>
      )}

      {/* Dance party (typed "danceparty") */}
      {discoGif && (
        <div
          onClick={() => setDiscoGif(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100003,
            backgroundColor: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <img
            src={discoGif}
            alt=""
            style={{
              maxWidth: '80vw',
              maxHeight: '80vh',
              borderRadius: '12px',
              boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
            }}
          />
        </div>
      )}

      {/* Admin broadcast strip. Renders nothing when there is nothing live,
          and suppresses itself while impersonating. Mounted in ShellLayout
          too: the launcher and /admin do not pass through AppShell. */}
      <BroadcastBanner />

      {/* Impersonation Banner */}
      {impersonation && (
        <div className={styles.impersonationBanner}>
          <PeopleSwapRegular style={{ fontSize: '16px' }} />
          <span>
            Viewing as <strong>{impersonation.targetName}</strong> ({impersonation.targetEmail})
          </span>
          <button
            className={styles.impersonationStopBtn}
            onClick={handleStopImpersonation}
            disabled={stoppingImpersonation}
          >
            {stoppingImpersonation ? <Spinner size="extra-tiny" /> : <DismissRegular style={{ fontSize: '12px' }} />}
            Stop
          </button>
        </div>
      )}

      {/* Main Layout */}
      <div className={styles.mainLayout}>
        {/* Sidebar */}
        <nav className={mergeClasses(styles.sidebar, sidebarCollapsed && styles.sidebarCollapsed)}>
          <div className={styles.navGroup}>
            {navItems.map((item, idx) => {
              if (isNavSection(item)) {
                if (sidebarCollapsed) {
                  return <div key={`section-${idx}`} className={styles.navSectionDivider} role="separator" />;
                }
                if (!item.collapsible) {
                  return (
                    <div key={`section-${idx}`} className={styles.navSectionHeader} role="presentation">
                      {item.label}
                    </div>
                  );
                }
                const open = isSectionOpen(item);
                const hiddenBadges = open ? 0 : (sectionBadgeTotals.get(item.label) ?? 0);
                return (
                  <button
                    key={`section-${idx}`}
                    type="button"
                    className={mergeClasses(styles.navSectionHeader, styles.navSectionToggle)}
                    aria-expanded={open}
                    onClick={() => toggleSection(item.label, !open)}
                  >
                    <ChevronRightRegular
                      className={styles.navSectionChevron}
                      style={open ? { transform: 'rotate(90deg)' } : undefined}
                    />
                    {item.label}
                    {hiddenBadges > 0 && (
                      <span className={styles.navBadge} style={{ marginLeft: 'auto' }}>
                        {hiddenBadges}
                      </span>
                    )}
                  </button>
                );
              }
              // Hidden by a closed section, unless this is the page you are on:
              // collapsing a section should tidy the list, never hide where you
              // currently are.
              const governing = sectionForIndex[idx];
              if (governing && !isSectionOpen(governing) && !entryIsActive(item)) return null;
              const Icon = item.icon;
              const childActive = item.children?.some((c) => isActive(c.path)) ?? false;
              const active = isActive(item.path) || childActive;

              if (item.disabled) {
                return (
                  <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                    <div
                      className={mergeClasses(
                        styles.navItem,
                        styles.navItemDisabled,
                        sidebarCollapsed && styles.navItemCollapsed,
                      )}
                    >
                      <Icon className={styles.navIcon} />
                      <span className={mergeClasses(styles.navLabel, sidebarCollapsed && styles.navLabelHidden)}>{item.label}</span>
                      {!sidebarCollapsed && item.badge && <span className={styles.navBadge}>{item.badge}</span>}
                    </div>
                  </Tooltip>
                );
              }

              const navLink = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={mergeClasses(
                    styles.navItem,
                    sidebarCollapsed && styles.navItemCollapsed,
                    active && (sidebarCollapsed ? styles.navItemSelectedCollapsed : styles.navItemSelected),
                  )}
                >
                  <Icon className={styles.navIcon} />
                  <span className={mergeClasses(styles.navLabel, sidebarCollapsed && styles.navLabelHidden)}>{item.label}</span>
                  {!sidebarCollapsed && item.badge && <span className={styles.navBadge}>{item.badge}</span>}
                </NavLink>
              );

              if (sidebarCollapsed) {
                // Items with children: show flyout popover with child links
                if (item.children && item.children.length > 0) {
                  return (
                    <Popover key={item.path} positioning="after" withArrow openOnHover mouseLeaveDelay={200}>
                      <PopoverTrigger disableButtonEnhancement>
                        {navLink}
                      </PopoverTrigger>
                      <PopoverSurface style={{
                        padding: '8px 0', minWidth: '160px',
                        backgroundColor: dark.bgBase, border: `1px solid ${dark.bgElevated}`,
                        borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                      }}>
                        <div style={{ padding: '4px 12px 8px', fontSize: '11px', fontWeight: 600, color: dark.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {item.label}
                        </div>
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            style={{
                              display: 'block', padding: '8px 16px', fontSize: '13px',
                              color: isActive(child.path) ? '#fff' : 'rgba(255,241,224,0.85)',
                              textDecoration: 'none', fontWeight: isActive(child.path) ? 600 : 400,
                              backgroundColor: isActive(child.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isActive(child.path) ? 'rgba(255,255,255,0.1)' : 'transparent'; }}
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </PopoverSurface>
                    </Popover>
                  );
                }
                // Items without children: simple tooltip
                return (
                  <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                    {navLink}
                  </Tooltip>
                );
              }

              if (item.children && item.children.length > 0) {
                const anyChildActive = item.children.some((c) => isActive(c.path));
                const showChildren = active || anyChildActive;
                return (
                  <div key={item.path}>
                    {navLink}
                    {showChildren && item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={mergeClasses(
                          styles.navSubItem,
                          isActive(child.path) && styles.navSubItemSelected,
                        )}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                );
              }

              return navLink;
            })}
            {/* The module's own guide, in the same place in every module
                (Sasha, 2026-08-16), linking to the module's guide PAGE: the
                whole documentation set in the module's own chrome, not one
                article in a modal and not a trip to Academy. Renders only
                where documentation exists, so no module shows a dead entry. */}
            <ModuleGuideNavItem
              module={appId}
              basePath={guideBasePath ?? `/${appId}`}
              collapsed={sidebarCollapsed}
              className={mergeClasses(
                styles.navItem,
                sidebarCollapsed && styles.navItemCollapsed
              )}
              activeClassName={mergeClasses(
                styles.navItemSelected,
                sidebarCollapsed && styles.navItemSelectedCollapsed
              )}
              iconClassName={styles.navIcon}
            />
          </div>
          <button
            type="button"
            className={styles.seasonalTrigger}
            onClick={handleSeasonalRain}
            aria-label={seasonal.label}
          />
          {/* ^ deliberately empty: discoverable only by the cursor change on hover, no tooltip */}
          <button
            className={styles.collapseToggle}
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={headerColor ? { backgroundColor: headerColor, ...(lightHeader ? { color: veracityColors.midnightCharcoal } : null) } : undefined}
          >
            {sidebarCollapsed ? <ChevronRightRegular /> : <ChevronLeftRegular />}
          </button>
        </nav>

        {/* Content */}
        <main className={styles.content} style={zoom !== 1 ? { zoom } : undefined}>{children}</main>
      </div>

      {/* Help Dialog */}
      <Dialog open={helpDialogOpen} onOpenChange={(_, d) => setHelpDialogOpen(d.open)}>
        <DialogSurface style={{ maxWidth: '500px' }}>
          <DialogBody>
            <DialogTitle>Help & Support</DialogTitle>
            <DialogContent>
              {helpPanel.sections.map((section) => (
                <div key={section.id} className={styles.helpSection}>
                  <div className={styles.helpSectionTitle}>{section.title}</div>
                  {section.links.map((link) => {
                    const Icon = HELP_PANEL_ICON_COMPONENTS[link.icon] ?? LinkRegular;
                    const kind = classifyHelpHref(link.href);
                    const inert = !link.enabled || kind === 'empty';
                    const content = (
                      <>
                        <Icon className={styles.helpLinkIcon} />
                        <div className={styles.helpLinkText}>
                          <div className={styles.helpLinkTitle}>{link.label}</div>
                          {link.description && (
                            <div className={styles.helpLinkDesc}>{link.description}</div>
                          )}
                        </div>
                      </>
                    );
                    if (inert) {
                      return (
                        <div
                          key={link.id}
                          className={styles.helpLink}
                          style={{ cursor: 'default', opacity: 0.45 }}
                        >
                          {content}
                        </div>
                      );
                    }
                    return renderHelpLink(link, kind, content, styles.helpLink, () => {
                      setHelpDialogOpen(false);
                      navigate(link.href);
                    });
                  })}
                </div>
              ))}
              {helpPanel.footerNote && (
                <div style={{
                  padding: '10px 12px',
                  marginBottom: '12px',
                  backgroundColor: COLORS.bgSurface,
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: COLORS.textSecondary,
                  whiteSpace: 'pre-wrap',
                }}>
                  {helpPanel.footerNote}
                </div>
              )}
              <div style={{
                padding: '12px',
                backgroundColor: COLORS.bgSurface,
                borderRadius: '6px',
                fontSize: '12px',
                color: COLORS.textSecondary
              }}>
                <strong>Version:</strong> 1.0.0<br />
                <strong>Last Updated:</strong> {new Date().toLocaleDateString()}<br />
                <strong>Logged in as:</strong> {email ?? ''}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setHelpDialogOpen(false)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Impersonate User Dialog */}
      <Dialog open={impersonateDialogOpen} onOpenChange={(_, d) => { setImpersonateDialogOpen(d.open); if (!d.open) { setImpersonateSearch(''); setImpersonateResults([]); } }}>
        <DialogSurface style={{ maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>View as Another User</DialogTitle>
            <DialogContent>
              <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '12px' }}>
                Search for a user to view the app from their perspective. Dataverse will apply their security roles.
              </p>
              <Input
                placeholder="Search by name or email..."
                value={impersonateSearch}
                onChange={(_, data) => setImpersonateSearch(data.value)}
                contentBefore={<SearchRegular />}
                style={{ width: '100%' }}
                autoFocus
              />
              <div className={styles.userSearchResults}>
                {impersonateLoading && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Spinner size="small" label="Searching..." />
                  </div>
                )}
                {!impersonateLoading && impersonateSearch.length >= 2 && impersonateResults.length === 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px' }}>
                    No users found
                  </div>
                )}
                {!impersonateLoading && impersonateResults.map((u) => (
                  <div
                    key={u.oid}
                    className={styles.userSearchItem}
                    onClick={() => handlePickUser(u)}
                  >
                    <div className={styles.userSearchAvatar}>
                      {getInitials(u.name)}
                    </div>
                    <div className={styles.userSearchInfo}>
                      <div className={styles.userSearchName}>{u.name}</div>
                      <div className={styles.userSearchEmail}>{u.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImpersonateDialogOpen(false)}>Cancel</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

export default AppShell;
